import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, MessageCircle, ShieldCheck, Award, BadgeCheck, Clock, ArrowLeftRight } from "lucide-react";
import { SITE, waUrl } from "@/lib/site";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] },
  }),
};

const badges = [
  { icon: Clock, label: `+${SITE.yearsExperience} anos de experiência` },
  { icon: ShieldCheck, label: "Validade jurídica nacional" },
  { icon: Award, label: "Assinatura GOV.BR Ouro" },
  { icon: BadgeCheck, label: "Atendimento urgente" },
];

const Hero = () => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden"
      style={{
        backgroundImage: `linear-gradient(180deg, rgba(250,250,250,0.92) 0%, rgba(250,250,250,0.98) 60%, #FAFAFA 100%), url(${SITE.media.paperTexture})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        {/* Left content */}
        <div className="lg:col-span-7">
          <motion.div initial="hidden" animate="show" variants={fadeUp} custom={0} className="overline mb-6">
            <span className="gold-rule mr-3 align-middle" />
            {SITE.brandName} · {SITE.matricula}
          </motion.div>

          <motion.h1
            initial="hidden"
            animate="show"
            variants={fadeUp}
            custom={1}
            className="font-serif-display tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[#0A1128]"
            data-testid="hero-title"
          >
            Tradutor e Intérprete <br className="hidden sm:block" />
            <span className="italic font-normal">Juramentado</span>
            <br />
            <span className="text-[#C5A059]">Espanhol</span>{" "}
            <ArrowLeftRight
              size={40}
              strokeWidth={1.5}
              className="inline-block align-middle text-[#0A1128] mx-2"
            />{" "}
            <span className="text-[#C5A059]">Português</span>
          </motion.h1>

          <motion.p
            initial="hidden"
            animate="show"
            variants={fadeUp}
            custom={2}
            className="mt-7 max-w-xl text-[#475569] text-base lg:text-lg leading-relaxed"
            data-testid="hero-subtitle"
          >
            Mais de {SITE.yearsExperience} anos de experiência com validade jurídica
            em todo o Brasil. Atendimento personalizado por{" "}
            <span className="text-[#0A1128] font-medium">{SITE.professionalName}</span> —
            credenciado pela Justiça Federal e JUCEA-AM.
          </motion.p>

          <motion.div
            initial="hidden"
            animate="show"
            variants={fadeUp}
            custom={3}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href={waUrl(`Olá ${SITE.professionalName.split(" ")[0]}, gostaria de solicitar uma tradução juramentada.`)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-btn"
              className="group inline-flex items-center gap-3 bg-[#25D366] text-white px-7 py-4 rounded-sm hover:bg-[#1DA851] transition-all hover:-translate-y-0.5 shadow-[0_8px_24px_-12px_rgba(37,211,102,0.6)]"
            >
              <MessageCircle size={18} />
              <span className="font-medium tracking-wide">Solicitar Tradução via WhatsApp</span>
            </a>

            <a
              href="#contato"
              data-testid="hero-quote-btn"
              className="group inline-flex items-center gap-2 border border-[#0A1128] text-[#0A1128] px-7 py-4 rounded-sm hover:bg-[#0A1128] hover:text-white transition-colors"
            >
              <span className="font-medium tracking-wide">Pedir Orçamento</span>
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </a>
          </motion.div>

          {/* Badges */}
          <motion.ul
            initial="hidden"
            animate="show"
            variants={fadeUp}
            custom={4}
            className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl"
            data-testid="hero-badges"
          >
            {badges.map((b, i) => {
              const Icon = b.icon;
              return (
                <li
                  key={i}
                  className="flex flex-col items-start gap-2 border-t border-[#E2E8F0] pt-4"
                >
                  <Icon size={18} strokeWidth={1.5} className="text-[#C5A059]" />
                  <span className="text-xs sm:text-sm text-[#0A1128] leading-tight">
                    {b.label}
                  </span>
                </li>
              );
            })}
          </motion.ul>
        </div>

        {/* Right: portrait + seal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 30 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
          className="lg:col-span-5 relative"
        >
          <div className="relative aspect-[4/5] max-w-md mx-auto">
            <div className="absolute -top-4 -left-4 right-8 bottom-8 border border-[#C5A059]/60 rounded-sm pointer-events-none" />
            <img
              src={SITE.media.portrait}
              alt={`${SITE.professionalName} — Tradutor Juramentado`}
              data-testid="hero-portrait"
              className="relative w-full h-full object-cover rounded-sm shadow-[0_30px_80px_-30px_rgba(10,17,40,0.45)]"
              loading="eager"
            />
            {/* Floating seal */}
            <motion.div
              initial={{ opacity: 0, rotate: -12, y: 20 }}
              animate={{ opacity: 1, rotate: -8, y: 0 }}
              transition={{ delay: 0.7, duration: 0.9 }}
              className="absolute -bottom-8 -right-6 w-28 md:w-36 bg-white p-3 rounded-full shadow-2xl border border-[#E2E8F0]"
            >
              <img
                src={SITE.media.seal}
                alt="Selo institucional JUCEA-AM"
                className="w-full h-full object-contain"
              />
            </motion.div>

            {/* Name plate */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6, duration: 0.8 }}
              className="absolute -bottom-6 -left-6 bg-[#0A1128] text-white px-5 py-4 rounded-sm max-w-[220px]"
            >
              <p className="text-[10px] tracking-[0.25em] uppercase text-[#C5A059] mb-1">
                Tradutor responsável
              </p>
              <p className="font-serif-display text-lg leading-tight">
                {SITE.professionalName}
              </p>
              <p className="text-[11px] text-white/70 mt-1">{SITE.matricula}</p>
            </motion.div>
          </div>
        </motion.div>
      </div>

      {/* Bottom subtle divider */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 mt-20">
        <div className="h-px bg-gradient-to-r from-transparent via-[#C5A059]/40 to-transparent" />
      </div>
    </section>
  );
};

export default Hero;
